import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useTheme } from "@mui/material/styles";
import FormItem from "@components/FormItem/FormItem";
import FormSection from "@components/FormSection/FormSection";
import { useNotification } from "@context/NotificationContext";
import type { FormContext } from "@features/port-security/components/SecurityClearence.types.ts";
import { buildCountryOptions, NO_PASSPORT_KEY } from "@features/port-security/components/utils";
import type { PortSecurityFormData } from "@features/port-security/schema/portSecurityFormSchema";
import { usePortSecurityFormStore } from "@features/port-security/store/usePortSecurityForm.store";
import { useFormattedReference } from "@hooks/useFormattedReference.ts";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import { REFERENCE_DATA_TYPES } from "@services/referenceDataApi.ts";
import { checkPassportOrEmiratesId } from "@services/securityClearanceApi";
import { useMutation } from "@tanstack/react-query";
import { runFieldNavigation } from "@telemetry/runFieldNavigation";
import { stringToTitleCase } from "@utils/formatters/strings";
import { t } from "i18next";
import { FormProvider, useWatch } from "react-hook-form";
import { useOutletContext } from "react-router-dom";

interface IdCheckResponse {
  status?: string;
  expirationDate?: string;
  isPassportOrEmiratesIdNumberDuplicate: boolean;
}
const LANG_BASE = "security-clearance";
const service = "bff-gatepass";
const UAE_NATIONALITY = "United Arab Emirates";
const PASSPORT_GROUP_FIELDS: Array<keyof PortSecurityFormData> = [
  "passportPlaceOfIssue",
  "passportNumber",
  "passportDateOfIssue",
  "passportExpiryDate",
];

const hasPassportValue = (value: unknown) => {
  if (value instanceof Date) {
    return !Number.isNaN(value.getTime());
  }

  return String(value ?? "").trim().length > 0;
};

const PASSPORT_FIELD_VALIDATION_CONTEXT = {
  module: "security_clearance",
  screen: "Security Clearance Passport Details",
  action: "validate_passport_number",
  fromField: "passportNumber",
  toField: "passportDuplicateStatus",
  entityType: "security_clearance",
};

const PassportData: React.FC = () => {
  const { formData, updateFormData } = useOutletContext<FormContext>();
  const previousPassportFieldsRef = useRef<{
    presentNationality?: string;
    passportPlaceOfIssue?: string;
    passportNumber?: string;
    passportDateOfIssue?: Date | string | null;
    passportExpiryDate?: Date | string | null;
  } | null>(null);
  const { options: countries } = useFormattedReference<{
    countryName: string;
    countryCode: string;
  }>(REFERENCE_DATA_TYPES.COUNTRIES, "bff-gatepass");
  const countriesOptions = useMemo(
    () => buildCountryOptions(countries, { valueMode: "code", formatName: stringToTitleCase }),
    [countries],
  );
  const { securityClearanceRenewID, isPassportNumberSet } = useAuthStore();
  const store = usePortSecurityFormStore();
  const hasPassportNumberValue = !!store.passportNumber;
  const { notify } = useNotification();
  const [status, setStatus] = useState<string | null>(null);
  const [expirationDate, setExpirationDate] = useState<string | null>(null);
  const theme = useTheme();
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);
  const presentNationality = useWatch({
    control: formData.control,
    name: "presentNationality",
  });
  const passportPlaceOfIssue = useWatch({
    control: formData.control,
    name: "passportPlaceOfIssue",
  });
  const passportNumber = useWatch({
    control: formData.control,
    name: "passportNumber",
  });
  const passportDateOfIssue = useWatch({
    control: formData.control,
    name: "passportDateOfIssue",
  });
  const passportExpiryDate = useWatch({
    control: formData.control,
    name: "passportExpiryDate",
  });
  // check whether coming from no passport path
  const fromNoPassport: "true" | null = localStorage.getItem(NO_PASSPORT_KEY) as "true" | null;
  // keep track of the current passport number
  const passportNumberRef = useRef(passportNumber);
  passportNumberRef.current = passportNumber;

  const checkPassportNumber = useMutation({
    mutationFn: async ({
      service,
      emiratesId,
      id,
      trackFieldNavigation,
    }: {
      service: string;
      emiratesId?: string;
      id?: string;
      trackFieldNavigation?: boolean;
    }) => {
      const execute = () =>
        checkPassportOrEmiratesId({
          service,
          emiratesId,
          id,
        }) as Promise<IdCheckResponse>;

      if (trackFieldNavigation) {
        return runFieldNavigation(PASSPORT_FIELD_VALIDATION_CONTEXT, execute);
      }

      return execute();
    },
    onSuccess: async (data: IdCheckResponse, variables) => {
      // only update SC status if it refers to the current passport number
      if (variables.id === passportNumberRef.current) {
        const { isPassportOrEmiratesIdNumberDuplicate, status, expirationDate } = data;

        if (isPassportOrEmiratesIdNumberDuplicate && status) {
          setStatus(status);
          if (expirationDate) {
            setExpirationDate(expirationDate);
          }
        } else {
          setStatus(null);
        }
      }
    },
    onError: (error: Error) => {
      notify({
        title: t(`${LANG_BASE}.notifications.check-id-error-title`),
        description: error.message,
        type: "error",
      });
    },
  });

  const handlePassportNumberChange = useCallback(
    (value: string) => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      const timer = setTimeout(() => {
        if (value && value.length >= 6 && value.length <= 10 && securityClearanceRenewID === null) {
          checkPassportNumber.reset();
          checkPassportNumber.mutate({ service, id: value, trackFieldNavigation: true });
        } else {
          setStatus(null);
        }
      }, 500);
      setDebounceTimer(timer);
    },
    [debounceTimer, checkPassportNumber],
  );

  useEffect(() => {
    const storePassport = store.passportNumber;
    if (storePassport && storePassport !== passportNumber) {
      formData.setValue("passportNumber", storePassport, { shouldDirty: true });
    }
    if (
      passportNumber &&
      passportNumber.length >= 6 &&
      passportNumber.length <= 10 &&
      securityClearanceRenewID === null
    ) {
      checkPassportNumber.reset();
      checkPassportNumber.mutate({ service, id: passportNumber });
    } else {
      setStatus(null);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, [debounceTimer]);

  useEffect(() => {
    const previous = previousPassportFieldsRef.current;
    previousPassportFieldsRef.current = {
      presentNationality,
      passportPlaceOfIssue,
      passportNumber,
      passportDateOfIssue,
      passportExpiryDate,
    };

    if (!previous) {
      return;
    }

    const isUaeNationality = presentNationality?.trim() === UAE_NATIONALITY;
    const didClearPassportField =
      (hasPassportValue(previous.passportPlaceOfIssue) &&
        !hasPassportValue(passportPlaceOfIssue)) ||
      (hasPassportValue(previous.passportNumber) && !hasPassportValue(passportNumber)) ||
      (hasPassportValue(previous.passportDateOfIssue) && !hasPassportValue(passportDateOfIssue)) ||
      (hasPassportValue(previous.passportExpiryDate) && !hasPassportValue(passportExpiryDate));

    if (!isUaeNationality || !didClearPassportField) {
      return;
    }

    void formData.trigger?.(PASSPORT_GROUP_FIELDS);
  }, [
    formData,
    passportDateOfIssue,
    passportExpiryDate,
    passportNumber,
    passportPlaceOfIssue,
    presentNationality,
  ]);

  const renderMessage = () => {
    switch (status) {
      case "APPROVED":
        return (
          <div
            className="flex flex-row my-2 items-center"
            style={{ color: theme.palette.success.main }}
          >
            <CheckCircleOutlineIcon className="mr-1" />
            <p>{t(`${LANG_BASE}.passport-no-or-emirates-id-approved`, { date: expirationDate })}</p>
          </div>
        );
      case "PERMANENTLY_REJECTED":
      case "SUSPENDED":
        return (
          <div
            className="flex flex-row my-2 items-center"
            style={{ color: theme.palette.error.main }}
          >
            <HighlightOffIcon className="mr-1" />
            <p>{t(`${LANG_BASE}.passport-no-or-emirates-id-rejected`)}</p>
          </div>
        );
      case null:
        return null;
      default:
        return (
          <div
            className="flex flex-row my-2 items-center"
            style={{ color: theme.palette.warning.main }}
          >
            <WarningAmberIcon className="mr-1" />
            <p>{t(`${LANG_BASE}.passport-no-or-emirates-id-pending`)}</p>
          </div>
        );
    }
  };

  // disable passport number field if coming from any other source than "I don't have a passport" button
  // and the passport number is set and this is a new security clearance being created
  const isPassportNumberFieldDisabled =
    !fromNoPassport &&
    hasPassportNumberValue &&
    isPassportNumberSet &&
    securityClearanceRenewID === null;

  return (
    <FormProvider {...formData}>
      <FormSection title={t("security-clearance.particular-of-passport")}>
        <div className="flex flex-col">
          <FormItem<PortSecurityFormData, "passportNumber">
            name="passportNumber"
            type="text"
            label={t("security-clearance.passport-number")}
            testId="security-passportNumber"
            disabled={isPassportNumberFieldDisabled}
            onFieldChange={(key: string, value: unknown) => {
              updateFormData(key, value);
              handlePassportNumberChange(value as string);
            }}
            enforceUppercase
          />
          {status && (
            <div id="messages-wrapper" className="mb-6 w-full">
              {renderMessage()}
            </div>
          )}
        </div>
        <FormItem<PortSecurityFormData, "passportPlaceOfIssue">
          name="passportPlaceOfIssue"
          type="autocomplete"
          options={countriesOptions}
          label={t("security-clearance.place-of-issue")}
          testId="security-passportPlaceOfIssue"
          onFieldChange={updateFormData}
        />
        <FormItem<PortSecurityFormData, "passportExpiryDate">
          required
          name="passportExpiryDate"
          type="date-picker"
          label={t("security-clearance.expiry-date")}
          testId="security-passportExpiryDate"
          onFieldChange={updateFormData}
          timePicker={false}
          dateConstraint="not-past"
        />
        <FormItem<PortSecurityFormData, "passportDateOfIssue">
          required
          name="passportDateOfIssue"
          type="date-picker"
          label={t("security-clearance.date-of-issue")}
          testId="security-passportDateOfIssue"
          onFieldChange={updateFormData}
          timePicker={false}
          dateConstraint="not-future"
        />
      </FormSection>
    </FormProvider>
  );
};

export default PassportData;
